import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  Shield,
  Check,
  Sparkles,
  Zap,
  CreditCard,
  Mail,
  User,
  Phone,
  CheckCircle2,
  Infinity as InfinityIcon,
} from 'lucide-react';
import {
  orderBumps,
  BUNDLE_NAME,
  BUNDLE_PRICE,
  BUNDLE_ORIGINAL_PRICE,
  BUNDLE_DISCOUNT_PCT,
  ADDON_BUNDLES,
  fmt,
} from '../mockMeditation';
import BuyCelebration from './BuyCelebration';
import MeditationPurchaseNotifier from './MeditationPurchaseNotifier';

export default function MeditationCheckout() {
  const navigate = useNavigate();
  const [bumps, setBumps] = useState({});
  const [dealMode, setDealMode] = useState(null); // null | 'any3' | 'all5'
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderError, setOrderError] = useState('');

  const apiBase =
    window.__APP_CONFIG__?.apiBase ||
    process.env.REACT_APP_API_URL ||
    '';
  const selectedCount = Object.values(bumps).filter(Boolean).length;

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existing = document.querySelector('script[data-razorpay-checkout]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay Checkout script.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.dataset.razorpayCheckout = 'true';
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay Checkout script.'));
      document.body.appendChild(script);
    });

  const createRazorpayOrder = async () => {
    const response = await axios.post(`${apiBase}/api/razorpay-order`, {
      product_id: 'meditation_bundle',
      name: form.name,
      email: form.email,
      phone: form.phone,
      addon_ids: orderBumps.filter((bump) => bumps[bump.id]).map((bump) => bump.id),
      addon_deal: dealMode,
    });
    return response.data;
  };

  const confirmRazorpayPayment = async (paymentResponse) => {
    await axios.post(`${apiBase}/api/razorpay-confirm`, {
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_signature: paymentResponse.razorpay_signature,
    });
  };

  const toggleBump = (id) => {
    // Blocked when All-5 deal is active
    if (dealMode === 'all5') return;
    setBumps((b) => {
      const currentlyChecked = !!b[id];
      // In "any3" mode enforce a hard cap of 3
      if (dealMode === 'any3' && !currentlyChecked && selectedCount >= 3) {
        return b;
      }
      return { ...b, [id]: !currentlyChecked };
    });
  };

  const activateDeal = (mode) => {
    if (dealMode === mode) {
      // toggle off
      setDealMode(null);
      setBumps({});
      return;
    }
    if (mode === 'all5') {
      const all = {};
      orderBumps.forEach((b) => { all[b.id] = true; });
      setBumps(all);
      setDealMode('all5');
    } else if (mode === 'any3') {
      setBumps({});
      setDealMode('any3');
    }
  };

  const addonsSubtotal = useMemo(() => {
    if (dealMode === 'all5') return ADDON_BUNDLES.all5.price;
    if (dealMode === 'any3' && selectedCount === 3) return ADDON_BUNDLES.any3.price;
    // Individual pricing (also used when in any3 mode but not yet exactly 3 selected)
    return orderBumps.reduce((s, b) => s + (bumps[b.id] ? b.price : 0), 0);
  }, [dealMode, bumps, selectedCount]);

  const total = useMemo(() => BUNDLE_PRICE + addonsSubtotal, [addonsSubtotal]);

  const originalStackTotal = useMemo(
    () => BUNDLE_ORIGINAL_PRICE + orderBumps.reduce((s, b) => s + (bumps[b.id] ? b.total : 0), 0),
    [bumps]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return;

    setProcessing(true);
    setOrderError('');

    try {
      await loadRazorpayScript();
      const orderData = await createRazorpayOrder();

      const options = {
        key: orderData.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: BUNDLE_NAME,
        description: `Secure purchase for ${BUNDLE_NAME}`,
        order_id: orderData.order_id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        theme: {
          color: '#10b981',
        },
        handler: async (razorpayResponse) => {
          try {
            await confirmRazorpayPayment(razorpayResponse);
            setCelebrate(true);
          } catch (error) {
            console.error('Payment confirmation failed', error);
            setOrderError('Payment completed but verification failed. Please contact support.');
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setOrderError('Payment cancelled. No charge was made.');
            setProcessing(false);
          },
        },
      };

      const checkout = new window.Razorpay(options);
      checkout.on('payment.failed', (response) => {
        setOrderError(response?.error?.description || 'Payment failed. Please try again.');
        setProcessing(false);
      });
      checkout.open();
    } catch (error) {
      console.error('Checkout initialization failed', error);
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to initiate secure payment. Please try again later.';
      setOrderError(typeof message === 'string' ? message : 'Unable to initiate secure payment. Please try again later.');
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050813] text-white antialiased overflow-x-hidden">
      <style>{`
        @keyframes chk-boom { 0% { transform: scale(0) rotate(-25deg); opacity: 0; } 60% { transform: scale(1.15) rotate(5deg); opacity: 1; } 100% { transform: scale(1) rotate(-6deg); opacity: 1; } }
        @keyframes chk-slash { 0% { width: 0; opacity: 0; } 100% { width: 100%; opacity: 1; } }
        @keyframes chk-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.55); } 50% { box-shadow: 0 0 0 12px rgba(16,185,129,0); } }
        .chk-boom { animation: chk-boom .7s cubic-bezier(.16,1.36,.5,1) forwards; }
        .chk-slash::after { content: ''; position: absolute; left: 0; top: 50%; height: 3px; width: 100%; background: linear-gradient(90deg, transparent, #ef4444 15%, #ef4444 85%, transparent); border-radius: 2px; transform: translateY(-50%) rotate(-6deg); box-shadow: 0 0 10px rgba(239,68,68,0.9); animation: chk-slash .5s .2s ease-out both; }
        .chk-pulse { animation: chk-pulse 2s infinite; }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#050813]/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/meditation-bundle" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back to offer
          </Link>
          <div className="inline-flex items-center gap-2 text-slate-400 text-xs">
            <Lock size={12} className="text-emerald-300" /> Secure Checkout
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        {!success ? (
          <div className="grid lg:grid-cols-5 gap-8">
            {/* LEFT: order bumps + form */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Your Order</h1>
                <p className="mt-2 text-slate-400 text-sm">Complete your details below. Instant lifetime access delivered to your email.</p>
              </div>

              {/* Contact form */}
              <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 space-y-4">
                <div>
                  <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Your Details</label>
                </div>
                {orderError && (
                  <div className="rounded-2xl border border-rose-400/20 bg-rose-500/5 p-3 text-rose-100 text-sm">
                    {orderError}
                  </div>
                )}
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full Name"
                    className="w-full pl-9 pr-3 py-3 rounded-md bg-[#0a1120] border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/50 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email address"
                      className="w-full pl-9 pr-3 py-3 rounded-md bg-[#0a1120] border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/50 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Mobile / SMS number"
                      className="w-full pl-9 pr-3 py-3 rounded-md bg-[#0a1120] border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/50 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>

                {/* Order Bumps */}
                <div className="pt-3">
                  <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">One-time upgrade offers</label>
                </div>

                {/* Bundle Deal Selector — pushes AOV */}
                <div
                  data-testid="deal-selector"
                  className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/[0.08] via-emerald-500/[0.05] to-transparent p-4 md:p-5"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400 text-slate-900">
                      <Sparkles size={11} /> Best Value Deals
                    </span>
                    <span className="text-white font-bold text-sm md:text-base">Save big by bundling add-ons</span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    {/* Any 3 */}
                    <button
                      type="button"
                      data-testid="deal-any3"
                      onClick={() => activateDeal('any3')}
                      className={`text-left rounded-xl border-2 p-4 transition-all ${
                        dealMode === 'any3'
                          ? 'border-emerald-400 bg-emerald-500/[0.10] shadow-[0_10px_40px_-15px_rgba(16,185,129,0.6)]'
                          : 'border-white/10 bg-black/25 hover:border-emerald-400/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-extrabold text-sm md:text-base">Pick Any 3</div>
                          <div className="text-slate-400 text-[11px] md:text-xs mt-0.5">Choose your favourite 3 add-ons.</div>
                        </div>
                        <span
                          className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center flex-shrink-0 ${
                            dealMode === 'any3' ? 'bg-emerald-400 border-emerald-400' : 'border-white/30'
                          }`}
                        >
                          {dealMode === 'any3' && <Check size={12} className="text-slate-900" strokeWidth={3} />}
                        </span>
                      </div>
                      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-500 text-xs font-bold line-through decoration-rose-500 decoration-[2px]">
                          {fmt(ADDON_BUNDLES.any3.was)}
                        </span>
                        <span className="text-emerald-300 font-extrabold text-xl">{fmt(ADDON_BUNDLES.any3.price)}</span>
                      </div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-rose-300">
                        {ADDON_BUNDLES.any3.savingsLabel}
                      </div>
                    </button>

                    {/* All 5 */}
                    <button
                      type="button"
                      data-testid="deal-all5"
                      onClick={() => activateDeal('all5')}
                      className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                        dealMode === 'all5'
                          ? 'border-amber-300 bg-amber-500/[0.10] shadow-[0_10px_40px_-15px_rgba(251,191,36,0.7)]'
                          : 'border-amber-400/40 bg-amber-500/[0.05] hover:border-amber-300'
                      }`}
                    >
                      <span className="absolute -top-2 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-widest bg-gradient-to-r from-amber-400 to-emerald-300 text-slate-900 shadow-lg">
                        <Sparkles size={9} /> Best Value
                      </span>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-extrabold text-sm md:text-base">Unlock All 5</div>
                          <div className="text-slate-400 text-[11px] md:text-xs mt-0.5">Every add-on. Nothing left behind.</div>
                        </div>
                        <span
                          className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center flex-shrink-0 ${
                            dealMode === 'all5' ? 'bg-amber-300 border-amber-300' : 'border-white/30'
                          }`}
                        >
                          {dealMode === 'all5' && <Check size={12} className="text-slate-900" strokeWidth={3} />}
                        </span>
                      </div>
                      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-500 text-xs font-bold line-through decoration-rose-500 decoration-[2px]">
                          {fmt(ADDON_BUNDLES.all5.was)}
                        </span>
                        <span className="text-amber-200 font-extrabold text-xl">{fmt(ADDON_BUNDLES.all5.price)}</span>
                      </div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-rose-300">
                        {ADDON_BUNDLES.all5.savingsLabel}
                      </div>
                    </button>
                  </div>

                  {dealMode === 'any3' && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 text-xs font-semibold">
                      <Check size={12} /> Pick any 3 add-ons below · {selectedCount}/3 selected
                    </div>
                  )}
                  {dealMode === 'all5' && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-300/40 text-amber-100 text-xs font-semibold">
                      <Sparkles size={12} /> All 5 add-ons unlocked · locked at {fmt(ADDON_BUNDLES.all5.price)}
                    </div>
                  )}
                  {dealMode && (
                    <button
                      type="button"
                      onClick={() => activateDeal(dealMode)}
                      className="mt-2 text-slate-400 hover:text-slate-200 text-[11px] underline"
                    >
                      Clear deal &amp; buy add-ons individually
                    </button>
                  )}
                </div>

                {orderBumps.map((bump) => {
                  const checked = !!bumps[bump.id];
                  const inAny3 = dealMode === 'any3';
                  const inAll5 = dealMode === 'all5';
                  const disabled = inAll5 || (inAny3 && !checked && selectedCount >= 3);
                  return (
                    <label
                      key={bump.id}
                      data-testid={`bump-${bump.id}`}
                      className={`relative block rounded-2xl border p-5 transition-all ${
                        disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                      } ${
                        checked
                          ? 'border-emerald-400/60 bg-emerald-500/[0.06] shadow-[0_10px_40px_-15px_rgba(16,185,129,0.5)]'
                          : 'border-amber-400/25 bg-amber-500/[0.04] hover:border-amber-400/50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="pt-0.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleBump(bump.id)}
                            className="sr-only"
                          />
                          <span
                            className={`inline-flex w-6 h-6 rounded-md border-2 items-center justify-center transition-colors ${
                              checked ? 'bg-emerald-400 border-emerald-400' : 'border-amber-300/60 bg-transparent'
                            }`}
                          >
                            {checked && <Check size={14} className="text-slate-900" strokeWidth={3} />}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-amber-400/15 border border-amber-400/40 text-amber-200">
                              <Sparkles size={10} /> {bump.badge}
                            </span>
                            <h3 className="text-white font-extrabold text-base md:text-lg">{bump.title}</h3>
                          </div>
                          <p className="mt-1 text-slate-400 text-xs md:text-sm">{bump.tagline}</p>

                          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                            {bump.items.map((it) => (
                              <li key={it.title} className="flex items-start gap-2 text-[13px] text-slate-300">
                                <Check size={13} className="text-emerald-300 mt-0.5 flex-shrink-0" />
                                <span className="leading-snug">{it.title}</span>
                              </li>
                            ))}
                          </ul>

                          {/* Prominent side-by-side price: strike original vs. today's price */}
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <span className="text-slate-500 text-[10px] uppercase tracking-widest font-bold">Price:</span>
                            <span className="text-slate-400 text-base font-bold line-through decoration-rose-500 decoration-[2.5px]">
                              {fmt(bump.total)}
                            </span>
                            {(dealMode === 'all5' || (dealMode === 'any3' && selectedCount === 3 && checked)) ? (
                              <span
                                data-testid={`bump-${bump.id}-price`}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-300 to-emerald-300 text-slate-900 text-sm font-extrabold shadow-[0_6px_20px_-6px_rgba(251,191,36,0.6)]"
                              >
                                <Sparkles size={12} /> Included in {dealMode === 'all5' ? 'All-5 Deal' : 'Any-3 Deal'}
                              </span>
                            ) : (
                              <span
                                data-testid={`bump-${bump.id}-price`}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-300 to-amber-300 text-slate-900 text-sm font-extrabold shadow-[0_6px_20px_-6px_rgba(16,185,129,0.6)]"
                              >
                                <Sparkles size={12} /> Today {fmt(bump.price)}
                              </span>
                            )}
                          </div>

                          <div
                            className={`mt-3 text-sm font-semibold ${
                              checked ? 'text-emerald-300' : 'text-slate-300'
                            }`}
                          >
                            {bump.checkboxCopy}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}

                <button
                  type="submit"
                  disabled={processing}
                  data-testid="checkout-pay-button"
                  className="chk-pulse mt-2 w-full inline-flex items-center justify-center gap-3 py-4 rounded-full font-bold text-slate-900 text-base md:text-lg bg-gradient-to-r from-emerald-300 to-emerald-500 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)] hover:brightness-105 disabled:opacity-70 transition-all"
                >
                  <CreditCard size={20} />
                  {processing ? 'Processing Secure Payment...' : `Pay ${fmt(total)} & Get Instant Access`}
                </button>

                <div className="flex items-center justify-center gap-4 text-slate-400 text-xs pt-1">
                  <span className="inline-flex items-center gap-1"><Lock size={12} className="text-emerald-300" /> 256-bit SSL</span>
                  <span className="inline-flex items-center gap-1"><InfinityIcon size={12} className="text-emerald-300" /> Lifetime Access</span>
                  <span className="inline-flex items-center gap-1"><Zap size={12} className="text-amber-300 fill-amber-300" /> Instant Access</span>
                </div>
              </form>
            </div>

            {/* RIGHT: order summary */}
            <aside className="lg:col-span-2">
              <div className="sticky top-20 rounded-2xl border border-emerald-400/25 bg-gradient-to-b from-white/[0.05] to-transparent p-6" data-testid="order-summary">
                <h2 className="text-white font-extrabold text-xl">Order Summary</h2>

                <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-white font-bold text-sm">{BUNDLE_NAME}</div>
                      <div className="text-slate-400 text-xs mt-0.5">3 modules &middot; audio + eBook + workbooks</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-500 text-xs line-through decoration-rose-500 decoration-[2px]">
                        {fmt(BUNDLE_ORIGINAL_PRICE)}
                      </div>
                      <div className="text-emerald-300 font-extrabold text-lg" data-testid="summary-bundle-price">
                        {fmt(BUNDLE_PRICE)}
                      </div>
                    </div>
                  </div>
                </div>

                {(() => {
                  const dealActive =
                    dealMode === 'all5' ||
                    (dealMode === 'any3' && selectedCount === 3);
                  if (dealActive) {
                    const d = dealMode === 'all5' ? ADDON_BUNDLES.all5 : ADDON_BUNDLES.any3;
                    return (
                      <div className="mt-3 rounded-xl border-2 border-amber-300/50 bg-gradient-to-br from-amber-500/[0.10] to-emerald-500/[0.05] p-4 flex items-start justify-between gap-3">
                        <div>
                          <div className="text-white font-bold text-sm">{d.label}</div>
                          <div className="text-slate-400 text-xs mt-0.5">
                            {dealMode === 'all5'
                              ? 'Every add-on bundled together — best value'
                              : '3/3 add-ons selected — deal unlocked'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-500 text-xs line-through decoration-rose-500 decoration-[2px]">
                            {fmt(d.was)}
                          </div>
                          <div className="text-amber-200 font-extrabold" data-testid="summary-deal-price">
                            {fmt(d.price)}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (dealMode === 'any3') {
                    return (
                      <>
                        <div className="mt-3 rounded-xl border border-dashed border-emerald-400/40 bg-emerald-500/[0.03] p-3 text-emerald-100 text-xs">
                          <span className="font-semibold">Any-3 deal active.</span> Select {3 - selectedCount} more add-on{3 - selectedCount === 1 ? '' : 's'} below to unlock the {fmt(ADDON_BUNDLES.any3.price)} bundle price.
                        </div>
                        {orderBumps.map((b) =>
                          bumps[b.id] ? (
                            <div key={b.id} className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.05] p-4 flex items-start justify-between gap-3">
                              <div>
                                <div className="text-white font-bold text-sm">{b.title}</div>
                                <div className="text-slate-400 text-xs mt-0.5">Special add-on upgrade</div>
                              </div>
                              <div className="text-right">
                                <div className="text-slate-500 text-xs line-through decoration-rose-500 decoration-[2px]">
                                  {fmt(b.total)}
                                </div>
                                <div className="text-emerald-300 font-extrabold">{fmt(b.price)}</div>
                              </div>
                            </div>
                          ) : null
                        )}
                      </>
                    );
                  }
                  return orderBumps.map((b) =>
                    bumps[b.id] ? (
                      <div key={b.id} className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.05] p-4 flex items-start justify-between gap-3">
                        <div>
                          <div className="text-white font-bold text-sm">{b.title}</div>
                          <div className="text-slate-400 text-xs mt-0.5">Special add-on upgrade</div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-500 text-xs line-through decoration-rose-500 decoration-[2px]">
                            {fmt(b.total)}
                          </div>
                          <div className="text-emerald-300 font-extrabold">{fmt(b.price)}</div>
                        </div>
                      </div>
                    ) : null
                  );
                })()}

                <div className="my-5 h-px bg-white/10" />

                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Original Value</span>
                  <span className="text-slate-500 line-through decoration-rose-500 decoration-[2px]">
                    {fmt(originalStackTotal)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-slate-300">You&apos;re saving</span>
                  <span className="text-rose-300 font-semibold">
                    {fmt(originalStackTotal - total)}
                  </span>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <span className="text-white text-lg font-bold">Total Today</span>
                  <span
                    data-testid="checkout-total-price"
                    className="text-4xl font-extrabold bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent"
                  >
                    {fmt(total)}
                  </span>
                </div>

                <div className="mt-4 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[11px] font-bold">
                  {BUNDLE_DISCOUNT_PCT}% OFF LAUNCH PRICE
                </div>

                <div className="mt-5 text-slate-400 text-xs leading-relaxed">
                  Instant lifetime download links delivered to your email immediately upon payment. Universal MP3 &amp; PDF format &mdash; works on all devices.
                </div>
              </div>
            </aside>
          </div>
        ) : (
          // SUCCESS STATE
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-300" size={40} />
            </div>
            <h1 className="mt-6 text-3xl md:text-5xl font-extrabold text-white tracking-tight">
              Payment Successful! 🎉
            </h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Your <span className="text-emerald-300 font-semibold">Ultimate Mind-Mastery Library</span> is on its way to <span className="text-white font-semibold">{form.email}</span>.
              Your lifetime download links will arrive by email within the next few minutes.
            </p>
            <div className="mt-8 inline-flex flex-col gap-2 text-sm text-slate-400">
              <div className="inline-flex items-center gap-2"><Check size={16} className="text-emerald-300" /> Instant lifetime access</div>
              <div className="inline-flex items-center gap-2"><Check size={16} className="text-emerald-300" /> Universal MP3 &amp; PDF format</div>
              <div className="inline-flex items-center gap-2"><Check size={16} className="text-emerald-300" /> Works on all devices, forever</div>
            </div>
            <button
              onClick={() => navigate('/meditation-bundle')}
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm transition-colors"
            >
              Back to Bundle
            </button>
          </div>
        )}
      </div>

      {/* Live purchase notifier - checkout variant emphasizes addon buyers */}
      <MeditationPurchaseNotifier variant="checkout" />

      {/* Buy celebration overlay */}
      <BuyCelebration
        open={celebrate}
        price={total}
        onComplete={() => {
          setCelebrate(false);
          setSuccess(true);
        }}
      />
    </div>
  );
}
